<template>
  <div class="sub-navigation">
    <div class="sub-navigation-inner">
      <div class="sub-navigation-inner-left">
        <ul
          id="submenu-navigation"
          class="sub-navigation-list"
          role="menu"
        >
          <template v-if="navSubItems && navSubItems.length > 0">
            <li
              v-for="(subItem, i) in navSubItems"
              :key="i"
              class="sub-navigation-list-item"
              role="none"
            >
              <kf-link
                :ref="`subnavItem-${i}`"
                class="sub-navigation-list-link"
                :href="subItem.href"
                role="menuitem"
                tabindex="0"
                @keydown.up.prevent="focusPreviousItem(i)"
                @keydown.down.prevent="focusNextItem(i)"
                @keydown.esc.prevent="closeSub"
                @keydown.tab="handleTabKey(i, $event)"
              >
                {{ subItem.label }}
              </kf-link>
            </li>
          </template>
        </ul>
      </div>
      <div class="sub-navigation-inner-right">
        <ul class="sub-navigation-extra-list">
          <li
            v-for="(subExtraItem, i) in navSubExtraItems.slice(0, 2)"
            :key="i"
            class="sub-navigation-extra-list-item"
          >
            <img
              class="sub-navigation-extra-list-image"
              :src="subExtraItem.imageSrc"
              :alt="subExtraItem.imageAlt"
              aria-hidden="true"
            >
            <kf-link
              :ref="`subnavExtraItem-${i}`"
              class="sub-navigation-extra-list-link"
              type="bold"
              :href="subExtraItem.href"
              @keydown.tab="handleExtraItemTab(i, $event)"
            >
              {{ subExtraItem.label }}
            </kf-link>
            <p class="sub-navigation-extra-list-text">
              {{ subExtraItem.text }}
            </p>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import KfLink from '@/components/base/link/Link.vue';

export default {
  name: 'KfNavigationSub',
  components: {
    KfLink,
  },
  props: {
    navSubItems: {
      type: Array,
      default: () => [],
    },
    navSubExtraItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isClosing: false,
      mouseClickDetected: false
    };
  },
  created() {
    // Initialize the mouseClickDetected flag
    this.mouseClickDetected = false;
  },
  mounted() {
    document.addEventListener('keydown', this.handleGlobalKeydown);
    this.setupFocusTrap();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleGlobalKeydown);

    if (this.endFocusTrap) {
      this.endFocusTrap.removeEventListener('focus', this.onEndTrapFocus);
    }
  },
  methods: {
    focusFirstItem() {
      // Don't trigger auto-focus if a mouse click was detected
      if (this.mouseClickDetected) {
        this.mouseClickDetected = false;
        return;
      }

      // Otherwise, proceed with focusing the first item
      if (this.navSubItems && this.navSubItems.length > 0 && this.$refs['subnavItem-0']) {
        this.$nextTick(() => {
          if (Array.isArray(this.$refs['subnavItem-0'])) {
            this.$refs['subnavItem-0'][0].$el.focus();
          } else {
            this.$refs['subnavItem-0'].$el.focus();
          }
        });
      }
    },
    focusPreviousItem(currentIndex) {
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : 0;
      if (this.$refs[`subnavItem-${prevIndex}`]) {
        this.$nextTick(() => {
          if (Array.isArray(this.$refs[`subnavItem-${prevIndex}`])) {
            this.$refs[`subnavItem-${prevIndex}`][0].$el.focus();
          } else {
            this.$refs[`subnavItem-${prevIndex}`].$el.focus();
          }
        });
      }
    },
    focusNextItem(currentIndex) {
      const nextIndex = currentIndex < this.navSubItems.length - 1 ? currentIndex + 1 : currentIndex;
      if (this.$refs[`subnavItem-${nextIndex}`]) {
        this.$nextTick(() => {
          if (Array.isArray(this.$refs[`subnavItem-${nextIndex}`])) {
            this.$refs[`subnavItem-${nextIndex}`][0].$el.focus();
          } else {
            this.$refs[`subnavItem-${nextIndex}`].$el.focus();
          }
        });
      }
    },
    focusLastRegularItem() {
      const lastIndex = this.navSubItems.length - 1;
      if (this.$refs[`subnavItem-${lastIndex}`]) {
        this.$nextTick(() => {
          if (Array.isArray(this.$refs[`subnavItem-${lastIndex}`])) {
            this.$refs[`subnavItem-${lastIndex}`][0].$el.focus();
          } else {
            this.$refs[`subnavItem-${lastIndex}`].$el.focus();
          }
        });
      }
    },
    focusFirstExtraItem() {
      if (this.navSubExtraItems && this.navSubExtraItems.length > 0 && this.$refs['subnavExtraItem-0']) {
        this.$nextTick(() => {
          if (Array.isArray(this.$refs['subnavExtraItem-0'])) {
            this.$refs['subnavExtraItem-0'][0].$el.focus();
          } else {
            this.$refs['subnavExtraItem-0'].$el.focus();
          }
        });
      }
    },
    handleTabKey(currentIndex, event) {
      // Only handle forward tabbing (no shift key)
      if (!event.shiftKey) {
        // Check if we're on the last regular item
        if (currentIndex === this.navSubItems.length - 1) {
          // If we have extra items, focus the first one
          if (this.navSubExtraItems && this.navSubExtraItems.length > 0) {
            event.preventDefault();
            this.focusFirstExtraItem();
          }
          // Otherwise, close submenu and focus next main menu item
          else {
            event.preventDefault();
            this.isClosing = true;
            this.focusNextMainMenuItem();
          }
        }
      }
    },
    handleExtraItemTab(currentIndex, event) {
      // Only handle forward tabbing from the last extra item
      if (!event.shiftKey && currentIndex === Math.min(this.navSubExtraItems.length, 2) - 1) {
        event.preventDefault();
        this.isClosing = true;
        this.focusNextMainMenuItem();
      }
      // Handle backward tabbing from the first extra item
      else if (event.shiftKey && currentIndex === 0) {
        event.preventDefault();
        this.focusLastRegularItem();
      }
    },
    focusNextMainMenuItem() {
      const mainNavButtons = document.querySelectorAll('.main-navigation-list-link');
      const toggleButtons = document.querySelectorAll('.main-navigation-list-link.is-toggle');

      let activeButton = null;
      for (let i = 0; i < toggleButtons.length; i++) {
        if (toggleButtons[i].classList.contains('is-open')) {
          activeButton = toggleButtons[i];
          break;
        }
      }

      if (!activeButton) {
        this.$emit('closeSub');
        this.isClosing = false;
        return;
      }

      let activeIndex = -1;
      for (let i = 0; i < mainNavButtons.length; i++) {
        if (mainNavButtons[i] === activeButton) {
          activeIndex = i;
          break;
        }
      }

      if (activeIndex !== -1 && activeIndex < mainNavButtons.length - 1) {
        const nextButton = mainNavButtons[activeIndex + 1];
        this.$emit('closeSub');

        setTimeout(() => {
          try {
            nextButton.focus();
          } catch (error) {
            console.error('Failed to focus next button:', error);
          }
          this.isClosing = false;
        }, 100);
      } else {
        this.$emit('closeSub');
        this.isClosing = false;
      }
    },
    handleGlobalKeydown(event) {
      if (this.$el.classList.contains('is-open')) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.isClosing = true;

          const mainNavButtons = document.querySelectorAll('.main-navigation-list-link.is-toggle');
          let activeButton = null;

          for (let i = 0; i < mainNavButtons.length; i++) {
            if (mainNavButtons[i].classList.contains('is-open')) {
              activeButton = mainNavButtons[i];
              break;
            }
          }

          this.$emit('closeSub');

          if (activeButton) {
            setTimeout(() => {
              try {
                activeButton.focus();
              } catch (error) {
                console.error('Failed to focus active button:', error);
              }
              this.isClosing = false;
            }, 100);
          } else {
            this.isClosing = false;
          }
        }
      }
    },
    isElementInSubmenu(element) {
      if (!element) return false;

      const submenuElement = document.getElementById('submenu-navigation');
      const extraItemsElement = this.$el.querySelector('.sub-navigation-inner-right');
      return (submenuElement && submenuElement.contains(element)) ||
             (extraItemsElement && extraItemsElement.contains(element));
    },
    closeSub() {
      this.$emit('closeSub');
    },
    setupFocusTrap() {
      let container;

      if (this.navSubExtraItems && this.navSubExtraItems.length > 0) {
        container = this.$el.querySelector('.sub-navigation-inner-right');
      } else {
        container = this.$el.querySelector('.sub-navigation-inner-left');
      }

      if (!container) {
        return;
      }

      this.endFocusTrap = document.createElement('span');
      this.endFocusTrap.setAttribute('tabindex', '0');
      this.endFocusTrap.setAttribute('aria-hidden', 'true');
      this.endFocusTrap.style.position = 'absolute';
      this.endFocusTrap.style.width = '1px';
      this.endFocusTrap.style.height = '1px';
      this.endFocusTrap.style.padding = '0';
      this.endFocusTrap.style.margin = '-1px';
      this.endFocusTrap.style.overflow = 'hidden';
      this.endFocusTrap.style.clip = 'rect(0, 0, 0, 0)';
      this.endFocusTrap.style.whiteSpace = 'nowrap';

      container.appendChild(this.endFocusTrap);
      this.endFocusTrap.addEventListener('focus', this.onEndTrapFocus);
    },
    onEndTrapFocus() {
      this.isClosing = true;
      this.focusNextMainMenuItem();
    }
  },
};
</script>

<style lang="postcss">
.sub-navigation {
  @apply hidden absolute z-10 overflow-hidden left-0 bottom-0 w-full bg-white;
  transform: translateY(100%);

  @apply hidden;

  &.is-open {
    @media (min-width: 1024px) {
      @apply flex shadow-md;
    }
  }

  &::before {
    content: "";
    @apply absolute left-0 w-full shadow-md;
    top: -10px;
    height: 10px;
  }

  &::after {
    content: "";
    @apply absolute left-0 w-full shadow-md bg-white;
    top: -20px;
    height: 20px;
  }

  &-inner {
    @apply relative w-full mx-auto flex justify-end lg:container px-5 md:px-4 py-10;

    &-left {
      @apply p-0 pr-10 border-r border-gray-50;
      margin-left: 140px;
      width: 340px;

      @screen lg {
        margin-left: 140px;
        padding-right: 60px;
        width: 240px;
      }

      @screen xl {
        width: 340px;
      }
    }

    &-right {
      display: flex;
      flex-direction: row;
      flex: 1;
      width: calc(100% - 240px);
      margin: 0;
      padding-left: 40px;
      align-self: center;
      padding-left: 60px;
      width: calc(100% - 340px);
    }
  }

  &-list {
    &-item {
      @screen lg {
        @apply mb-3;
      }
      .sub-navigation-list-link {
        @apply no-underline;
        display: inline-block;
        padding: 0.25rem 0;

        &:hover {
          @apply underline;
        }

        /* Remove focus outline for all focus events */
        &:focus {
          outline: none;
          box-shadow: none;
        }

        /* Only show focus styles for keyboard navigation */
        &:focus-visible {
          outline: 2px solid theme('colors.primary.bravo');
          outline-offset: 2px;
          box-shadow: none;
        }
      }
    }
  }

  &-extra {
    &-list {
      @apply flex;
      align-self: center;

      &-item {
        width: 50%;

        &:first-child {
          @apply pr-16;
        }
      }

      &-image {
        @apply object-contain mb-5;
        width: 128px;
        height: 115px;
      }

      &-link {
        @apply text-xl mb-1;

        &.is-bold {
          &::before {
            top: 9px;
            background-size: 14px 14px;
          }
        }

        /* Remove focus outline for all focus events */
        &:focus {
          outline: none;
          box-shadow: none;
        }

        /* Only show focus styles for keyboard navigation */
        &:focus-visible {
          outline: 2px solid theme('colors.primary.bravo');
          outline-offset: 2px;
          box-shadow: none;
        }
      }

      &-text {
        @apply text-base;
      }
    }
  }
}
</style>
